import React, { useRef, useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode';
import { API_BASE_URL } from '../../../config.js';

export const resolveAssetUrl = (url) => {
  if (!url || typeof url !== 'string' || url.trim() === '') return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }
  const cleanPath = url.startsWith('/') ? url : `/${url}`;
  return `${API_BASE_URL}${cleanPath}`;
};

export const getDefaultSchoolLogo = (color = '#1e3a8a', name = 'S') => {
  const initial = (name && name.length > 0 ? name.trim().charAt(0) : 'S').toUpperCase();
  return 'data:image/svg+xml,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100">
      <defs>
        <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="${color}"/>
          <stop offset="100%" stop-color="#3b82f6"/>
        </linearGradient>
      </defs>
      <circle cx="50" cy="50" r="46" fill="url(#grad)" stroke="#d97706" stroke-width="4"/>
      <circle cx="50" cy="50" r="38" fill="none" stroke="#ffffff" stroke-width="1.5" stroke-dasharray="3,3"/>
      <text x="50" y="62" text-anchor="middle" font-size="36" font-weight="bold" fill="#ffffff" font-family="'Times New Roman', serif">${initial}</text>
    </svg>
  `);
};

export const getDefaultQrCode = (text = 'VERIFIED') => {
  return 'data:image/svg+xml,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80">
      <rect width="80" height="80" fill="#ffffff" stroke="#cbd5e1" stroke-width="2" rx="4"/>
      <rect x="10" y="10" width="20" height="20" fill="#1e3a8a"/>
      <rect x="14" y="14" width="12" height="12" fill="#ffffff"/>
      <rect x="17" y="17" width="6" height="6" fill="#1e3a8a"/>
      <rect x="50" y="10" width="20" height="20" fill="#1e3a8a"/>
      <rect x="54" y="14" width="12" height="12" fill="#ffffff"/>
      <rect x="57" y="17" width="6" height="6" fill="#1e3a8a"/>
      <rect x="10" y="50" width="20" height="20" fill="#1e3a8a"/>
      <rect x="14" y="54" width="12" height="12" fill="#ffffff"/>
      <rect x="17" y="57" width="6" height="6" fill="#1e3a8a"/>
      <rect x="36" y="10" width="8" height="8" fill="#1e3a8a"/>
      <rect x="36" y="24" width="8" height="8" fill="#1e3a8a"/>
      <rect x="10" y="36" width="8" height="8" fill="#1e3a8a"/>
      <rect x="24" y="36" width="8" height="8" fill="#1e3a8a"/>
      <rect x="36" y="36" width="8" height="8" fill="#1e3a8a"/>
      <rect x="50" y="36" width="8" height="8" fill="#1e3a8a"/>
      <rect x="62" y="36" width="8" height="8" fill="#1e3a8a"/>
      <rect x="36" y="50" width="8" height="8" fill="#1e3a8a"/>
      <rect x="50" y="50" width="8" height="8" fill="#1e3a8a"/>
      <rect x="62" y="62" width="8" height="8" fill="#1e3a8a"/>
    </svg>
  `);
};

const CertificateRenderer = ({
  id = 'certificate-render-target',
  certificateNumber = 'SC/2026/000001',
  issuedDate = new Date().toLocaleDateString('en-GB'),
  certificateType = 'STUDY_CERTIFICATE',
  certificateCategory = 'ACADEMIC',
  templateName = 'CLASSIC_SCHOOL',
  orientation = 'PORTRAIT',
  pageSize = 'A4',
  primaryColor,
  secondaryColor,
  signatureLayout = 'DUAL',
  logoUrl,
  sealUrl,
  principalSignatureUrl,
  classTeacherSignatureUrl,
  headmasterSignatureUrl,
  trustName = '',
  schoolName = '',
  schoolAddress = '',
  schoolPhone = '',
  schoolEmail = '',
  schoolWebsite = '',
  schoolRegistrationNo = '',
  udiseCode = '',
  principalName = '',
  headmasterName = '',
  classTeacherName = '',
  templateContent = '',
  studentData = {},
  academicYear = '2026-27',
  metadata = {},
  showQrCode = true,
  watermarkEnabled = false,
  isReprint = false,
  reprintCount = 0
}) => {
  const containerRef = useRef(null);

  const pColor = primaryColor || '#1e3a8a';
  const sColor = secondaryColor || '#d97706';

  // Reliable image URLs with resolution and fallback handling
  const defaultLogo = getDefaultSchoolLogo(pColor, schoolName);
  const resolvedLogoUrl = logoUrl ? resolveAssetUrl(logoUrl) : defaultLogo;
  const resolvedSealUrl = sealUrl ? resolveAssetUrl(sealUrl) : '';
  const resolvedPrincipalSig = principalSignatureUrl ? resolveAssetUrl(principalSignatureUrl) : '';
  const resolvedTeacherSig = classTeacherSignatureUrl ? resolveAssetUrl(classTeacherSignatureUrl) : '';

  const [currentLogo, setCurrentLogo] = useState(resolvedLogoUrl);
  const [currentSeal, setCurrentSeal] = useState(resolvedSealUrl);
  const [currentPrincipalSig, setCurrentPrincipalSig] = useState(resolvedPrincipalSig);
  const [currentTeacherSig, setCurrentTeacherSig] = useState(resolvedTeacherSig);
  const [currentQr, setCurrentQr] = useState('');

  useEffect(() => {
    setCurrentLogo(resolvedLogoUrl);
  }, [resolvedLogoUrl]);

  useEffect(() => {
    setCurrentSeal(resolvedSealUrl);
  }, [resolvedSealUrl]);

  useEffect(() => {
    setCurrentPrincipalSig(resolvedPrincipalSig);
  }, [resolvedPrincipalSig]);

  useEffect(() => {
    setCurrentTeacherSig(resolvedTeacherSig);
  }, [resolvedTeacherSig]);

  useEffect(() => {
    let isMounted = true;
    const generateCrispQr = async () => {
      try {
        const origin = typeof window !== 'undefined' && window.location && window.location.origin
          ? window.location.origin
          : 'https://scms.ssquareg.io';
        const verifyUrl = `${origin}/verify/certificate/${encodeURIComponent(certificateNumber)}`;
        
        // Generate high resolution 400px QR with Level H error correction (30% recovery)
        const dataUrl = await QRCode.toDataURL(verifyUrl, {
          width: 400,
          margin: 1,
          color: {
            dark: '#0f172a', // deep rich black-slate for maximum contrast and readability
            light: '#ffffff'
          },
          errorCorrectionLevel: 'H'
        });
        if (isMounted) {
          setCurrentQr(dataUrl);
        }
      } catch (err) {
        console.error('Failed to generate high-res QR code:', err);
      }
    };
    generateCrispQr();
    return () => { isMounted = false; };
  }, [certificateNumber]);

  // Dynamic Token Replacer
  const renderContent = () => {
    const rawText = templateContent || '';
    if (!rawText.trim()) {
      return (
        <span>
          This is to certify that Master/Miss <strong>{studentData.name || 'Rahul Kumar'}</strong>, 
          Admission No. <strong>{studentData.admissionNo || 'ADM-8921'}</strong>, 
          Son / Daughter of Shri <strong>{studentData.fatherName || 'Suresh Kumar'}</strong> and 
          Smt. <strong>{studentData.motherName || 'Sunita Devi'}</strong>, 
          is / was a bonafide student of this school studying in <strong>{studentData.className || 'Class 10'}</strong>, 
          Section <strong>{studentData.section || 'A'}</strong>, 
          Roll No. <strong>{studentData.rollNo || '14'}</strong> during the academic session <strong>{academicYear}</strong>.
          <br /><br />
          He / She bears a <strong>{metadata.conduct || 'Good'}</strong> moral character and satisfactory conduct during his/her stay at the institution. 
          We wish him/her all success in future endeavors.
        </span>
      );
    }

    const dict = {
      '[STUDENT_NAME]': studentData.name || 'Rahul Kumar',
      '[FATHER_NAME]': studentData.fatherName || 'Suresh Kumar',
      '[MOTHER_NAME]': studentData.motherName || 'Sunita Devi',
      '[GUARDIAN_NAME]': studentData.guardianName || studentData.fatherName || 'Suresh Kumar',
      '[DOB]': studentData.dob || '15/08/2012',
      '[GENDER]': studentData.gender || 'Male',
      '[ADMISSION_NO]': studentData.admissionNo || 'ADM-8921',
      '[ROLL_NO]': studentData.rollNo || '14',
      '[STUDENT_ID]': studentData.studentId || 'STU-2026-0042',
      '[CLASS_NAME]': studentData.className || 'Class 10',
      '[SECTION]': studentData.section || 'A',
      '[ACADEMIC_YEAR]': academicYear || '2026-27',
      '[DATE_OF_ADMISSION]': studentData.admissionDate || '01/06/2020',
      '[STUDENT_ADDRESS]': studentData.address || '#42, 3rd Cross, MG Road, Bengaluru',
      '[SCHOOL_NAME]': schoolName || 'School Name',
      '[SCHOOL_ADDRESS]': schoolAddress || '',
      '[SCHOOL_PHONE]': schoolPhone || '',
      '[SCHOOL_EMAIL]': schoolEmail || '',
      '[SCHOOL_WEBSITE]': schoolWebsite || '',
      '[SCHOOL_REGISTRATION_NO]': schoolRegistrationNo || '',
      '[UDISE_CODE]': udiseCode || '',
      '[PRINCIPAL_NAME]': principalName || 'Principal',
      '[HEADMASTER_NAME]': headmasterName || 'Headmaster',
      '[CLASS_TEACHER_NAME]': classTeacherName || 'Class Teacher',
      '[CERTIFICATE_NO]': certificateNumber || 'SC/2026/000001',
      '[ISSUE_DATE]': issuedDate || new Date().toLocaleDateString('en-GB'),
      '[REASON_FOR_LEAVING]': metadata.reasonForLeaving || 'Parent Relocation / Higher Studies',
      '[PROMOTION_STATUS]': metadata.promotionStatus || 'Promoted to Higher Class',
      '[CONDUCT]': metadata.conduct || 'Exemplary',
      '[SUBJECTS]': metadata.subjects || 'English, Mathematics, Science, Social Studies, Hindi',
      '[ATTENDANCE_PERCENTAGE]': metadata.attendancePercentage || '95.4%',
      '[TOTAL_WORKING_DAYS]': metadata.totalWorkingDays || '220',
      '[DAYS_PRESENT]': metadata.daysPresent || '210',
      '[EVENT_NAME]': metadata.eventName || 'Annual Olympiad',
      '[COMPETITION_NAME]': metadata.competitionName || 'State Debate Championship',
      '[POSITION]': metadata.position || '1st Position',
      '[AWARD_NAME]': metadata.awardName || 'Merit Trophy',
      '[TOTAL_FEE]': metadata.totalFee || '45,000',
      '[PAID_AMOUNT]': metadata.paidAmount || '45,000',
      '[OUTSTANDING_AMOUNT]': metadata.outstandingAmount || '0',
      '[FEE_STATUS]': metadata.feeStatus || 'Fully Paid / No Dues'
    };

    let processed = rawText;
    Object.keys(dict).forEach(key => {
      processed = processed.replaceAll(key, dict[key]);
    });

    return <span style={{ whiteSpace: 'pre-wrap' }}>{processed}</span>;
  };

  const isComprehensiveClearance = certificateType === 'COMPREHENSIVE_NO_DUES_CERTIFICATE' || 
                                   certificateType === 'STUDENT_CLEARANCE_SLIP' || 
                                   templateName === 'COMPREHENSIVE_CLEARANCE';

  const getSubjectList = () => {
    if (Array.isArray(metadata.subjectList) && metadata.subjectList.length > 0) {
      return metadata.subjectList;
    }
    if (metadata.subjects && typeof metadata.subjects === 'string' && metadata.subjects.trim()) {
      const parts = metadata.subjects.split(',').map(s => s.trim()).filter(Boolean);
      if (parts.length > 0) {
        return parts.map((subj) => ({
          name: subj,
          teacher: '',
          notesStatus: 'Complete & Verified',
          attendance: `${metadata.attendancePercentage || '95.4'}%`
        }));
      }
    }
    return [
      { name: 'English Language & Literature', teacher: '', notesStatus: 'Complete & Verified', attendance: '96%' },
      { name: 'Mathematics', teacher: '', notesStatus: 'Complete & Verified', attendance: '95%' },
      { name: 'General Science / EVS', teacher: '', notesStatus: 'Complete & Verified', attendance: '97%' },
      { name: 'Social Studies', teacher: '', notesStatus: 'Complete & Verified', attendance: '94%' },
      { name: 'Second Language (Regional)', teacher: '', notesStatus: 'Complete & Verified', attendance: '96%' },
      { name: 'Hindi / Third Language', teacher: '', notesStatus: 'Complete & Verified', attendance: '95%' },
      { name: 'Computer Science / IT', teacher: '', notesStatus: 'Complete & Verified', attendance: '98%' },
      { name: 'Physical Education & Health', teacher: '', notesStatus: 'Complete & Verified', attendance: '100%' }
    ];
  };

  const subjectListToRender = getSubjectList();

  const getCleanTitle = () => {
    if (isComprehensiveClearance) {
      return 'ALL-SUBJECT CLEARANCE & NO DUES CERTIFICATE';
    }
    const formatted = certificateType
      .replace(/_/g, ' ')
      .replace(/CERTIFICATE/g, '')
      .trim();
    return `${formatted} CERTIFICATE`.toUpperCase();
  };

  const isLandscape = orientation === 'LANDSCAPE' || templateName === 'LANDSCAPE_AWARD';
  const isTC = certificateType === 'TRANSFER_CERTIFICATE' || certificateType === 'SCHOOL_LEAVING_CERTIFICATE' || templateName === 'DEDICATED_TC';
  const isLetter = templateName === 'FORMAL_LETTER';

  // High-Resolution Crisp PDF Download (Zero-Overlap Off-Screen Rendering)
  const handleDownloadPDF = async () => {
    const el = containerRef.current;
    if (!el) return;
    try {
      // Create an unscaled offscreen wrapper attached directly to document.body
      // This completely strips any parent CSS transform: scale(0.80) that causes font kerning collapse in html2canvas
      const wrapper = document.createElement('div');
      wrapper.style.position = 'fixed';
      wrapper.style.left = '0';
      wrapper.style.top = '0';
      wrapper.style.zIndex = '-9999';
      wrapper.style.pointerEvents = 'none';
      wrapper.style.transform = 'none';
      wrapper.style.backgroundColor = '#ffffff';

      const clone = el.cloneNode(true);
      clone.style.transform = 'none';
      clone.style.margin = '0';
      clone.style.boxShadow = 'none';
      
      const targetWidth = isLandscape ? 1123 : 794; // Exact A4 standard pixel width at 96 DPI
      const targetHeight = isLandscape ? 794 : 1123; // Exact A4 standard pixel height at 96 DPI
      clone.style.width = `${targetWidth}px`;
      clone.style.minHeight = `${targetHeight}px`;
      clone.style.maxHeight = `${targetHeight}px`;

      // Normalize letterSpacing across all cloned child elements so html2canvas computes accurate glyph advances
      const allTextNodes = clone.querySelectorAll('*');
      allTextNodes.forEach(node => {
        if (node.style && node.style.letterSpacing) {
          node.style.letterSpacing = 'normal';
        }
      });

      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);

      // Wait 120ms for fonts and SVG/images in clone to settle
      await new Promise(resolve => setTimeout(resolve, 120));

      const canvas = await html2canvas(clone, {
        scale: 2, // 2x gives 300 DPI high-definition print quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: targetWidth,
        height: targetHeight,
        windowWidth: targetWidth,
        windowHeight: targetHeight
      });

      // Remove the off-screen clone from body
      document.body.removeChild(wrapper);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: isLandscape ? 'landscape' : 'portrait',
        unit: 'mm',
        format: pageSize.toLowerCase() === 'a5' ? 'a5' : 'a4',
        compress: true
      });

      const pdfWidth = isLandscape ? 297 : 210;
      const pdfHeight = isLandscape ? 210 : 297;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');

      const safeCertNo = (certificateNumber || 'certificate').replace(/[/\\?%*:|"<>]/g, '_');
      pdf.save(`${safeCertNo}_${studentData.name || 'Student'}.pdf`);
    } catch (err) {
      console.error('Failed to generate certificate PDF:', err);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', width: '100%' }}>
      
      {/* Action Buttons Toolbar (Screen Only) */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        maxWidth: isLandscape ? '1120px' : '794px',
        padding: '10px 16px',
        backgroundColor: '#1e293b',
        borderRadius: '12px',
        color: '#ffffff',
        boxSizing: 'border-box'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px' }}>
          <span style={{ backgroundColor: 'rgba(59, 130, 246, 0.25)', color: '#93c5fd', padding: '3px 8px', borderRadius: '4px', fontWeight: '700', textTransform: 'uppercase' }}>
            {certificateCategory}
          </span>
          <span style={{ fontFamily: 'monospace', color: '#cbd5e1', fontWeight: '700' }}>{certificateNumber}</span>
          {isReprint && (
            <span style={{ backgroundColor: 'rgba(245, 158, 11, 0.25)', color: '#fde68a', padding: '2px 6px', borderRadius: '4px', fontWeight: '800', fontSize: '10px' }}>
              REPRINT #{reprintCount}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            type="button"
            onClick={handlePrint}
            style={{
              padding: '6px 14px',
              backgroundColor: '#334155',
              color: '#ffffff',
              border: '1px solid #475569',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600'
            }}
          >
            🖨️ Direct Print
          </button>
          <button
            type="button"
            onClick={handleDownloadPDF}
            style={{
              padding: '6px 16px',
              backgroundColor: '#2563eb',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '700',
              boxShadow: '0 2px 8px rgba(37, 99, 235, 0.4)'
            }}
          >
            📥 Download Official PDF
          </button>
        </div>
      </div>

      {/* Target Printable Certificate Sheet (A4 Dimensions) */}
      <div
        id={id}
        ref={containerRef}
        style={{
          width: isLandscape ? '297mm' : '210mm',
          minHeight: isLandscape ? '210mm' : '297mm',
          maxHeight: isLandscape ? '210mm' : '297mm',
          padding: isLetter ? '18mm 22mm' : '12mm 14mm',
          backgroundColor: '#ffffff',
          color: '#0f172a',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
          position: 'relative',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          overflow: 'hidden',
          fontFamily: '"Times New Roman", Times, Georgia, serif'
        }}
      >
        {/* Background Watermark */}
        {watermarkEnabled && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            opacity: 0.04,
            pointerEvents: 'none',
            zIndex: 1
          }}>
            <img src={currentLogo} alt="" style={{ width: '320px', height: '320px', objectFit: 'contain', filter: 'grayscale(100%)' }} />
          </div>
        )}

        {/* Reprint Watermark */}
        {isReprint && (
          <div style={{
            position: 'absolute',
            top: '28px',
            right: '36px',
            border: '2px solid rgba(217, 119, 6, 0.4)',
            color: 'rgba(217, 119, 6, 0.7)',
            fontSize: '12px',
            fontFamily: 'monospace',
            fontWeight: '800',
            padding: '4px 10px',
            borderRadius: '4px',
            transform: 'rotate(12deg)',
            zIndex: 10
          }}>
            OFFICIAL REPRINT #{reprintCount}
          </div>
        )}

        {/* Certificate Outer Border Layout */}
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          boxSizing: 'border-box',
          border: isLetter ? 'none' : `3px solid ${pColor}`,
          padding: isLetter ? '0' : '10mm 12mm',
          outline: isLetter ? 'none' : `1px dashed ${sColor}`,
          outlineOffset: '-6px',
          zIndex: 2
        }}>
          
          {/* ================= HEADER SECTION ================= */}
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${pColor}35`, paddingBottom: '12px' }}>
            {/* Left School Logo */}
            <div style={{ width: '85px', height: '85px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img
                src={currentLogo}
                alt="School Logo"
                onError={() => setCurrentLogo(defaultLogo)}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            </div>

            {/* School Particulars */}
            <div style={{ flex: 1, textAlign: 'center', padding: '0 12px' }}>
              {(trustName || metadata.trustName) && (
                <div style={{
                  fontSize: '12px',
                  fontWeight: '700',
                  color: '#64748b',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  marginBottom: '2px',
                  fontFamily: '"Times New Roman", Times, Georgia, serif'
                }}>
                  {trustName || metadata.trustName}
                </div>
              )}
              <h1 style={{
                margin: '0 0 2px 0',
                fontSize: '22px',
                fontWeight: '800',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                color: pColor,
                lineHeight: '1.2',
                fontFamily: 'Arial, Helvetica, sans-serif'
              }}>
                {schoolName || 'DELHI PUBLIC SCHOOL'}
              </h1>
              {schoolAddress && (
                <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#475569', lineHeight: '1.3', fontFamily: 'Arial, Helvetica, sans-serif' }}>{schoolAddress}</p>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', fontSize: '10px', color: '#64748b', marginTop: '5px', flexWrap: 'wrap', fontFamily: 'Arial, Helvetica, sans-serif' }}>
                {schoolRegistrationNo && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ fontWeight: '700', color: '#334155' }}>Reg/Aff No:</span>
                    <span>{schoolRegistrationNo}</span>
                  </div>
                )}
                {udiseCode && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ fontWeight: '700', color: '#334155' }}>UDISE:</span>
                    <span>{udiseCode}</span>
                  </div>
                )}
                {schoolPhone && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ fontWeight: '700', color: '#334155' }}>Phone:</span>
                    <span>{schoolPhone}</span>
                  </div>
                )}
                {schoolEmail && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ fontWeight: '700', color: '#334155' }}>Email:</span>
                    <span>{schoolEmail}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Right QR Code Verification */}
            {showQrCode ? (
              <div style={{ width: '85px', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                <div style={{
                  width: '66px',
                  height: '66px',
                  backgroundColor: '#ffffff',
                  border: '1.5px solid #0f172a',
                  borderRadius: '5px',
                  padding: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                }}>
                  {currentQr ? (
                    <img
                      src={currentQr}
                      alt="Official Verification QR"
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <div style={{ fontSize: '8px', color: '#64748b' }}>Generating QR...</div>
                  )}
                </div>
                <span style={{ fontSize: '7.5px', color: '#0f172a', fontFamily: 'Arial, sans-serif', marginTop: '3px', fontWeight: '800', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                  Scan to Verify
                </span>
                <span style={{ fontSize: '6.5px', color: '#64748b', fontFamily: 'monospace' }}>
                  SCMS OFFICIAL QR
                </span>
              </div>
            ) : (
              <div style={{ width: '85px', flexShrink: 0 }} />
            )}
          </div>

          {/* ================= CERTIFICATE TITLE & META ================= */}
          <div style={{ width: '100%', textAlign: 'center', margin: '10px 0' }}>
            <div style={{ display: 'inline-block' }}>
              <h2 style={{
                margin: 0,
                fontSize: '15px',
                fontWeight: '800',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                padding: '4px 20px',
                borderBottom: `2px solid ${sColor}`,
                color: pColor,
                fontFamily: 'Arial, Helvetica, sans-serif'
              }}>
                {getCleanTitle()}
              </h2>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '10.5px', color: '#475569', marginTop: '8px', padding: '0 6px', fontFamily: 'Arial, Helvetica, sans-serif' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontWeight: '700', color: '#334155' }}>Certificate No:</span>
                <span style={{ fontFamily: 'monospace', color: '#0f172a', fontWeight: '800' }}>{certificateNumber}</span>
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontWeight: '700', color: '#334155' }}>Academic Year:</span>
                <span style={{ fontWeight: '700', color: '#0f172a' }}>{academicYear}</span>
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontWeight: '700', color: '#334155' }}>Date of Issue:</span>
                <span style={{ fontWeight: '700', color: '#0f172a' }}>{issuedDate}</span>
              </div>
            </div>
          </div>

          {/* ================= MAIN BODY SECTION ================= */}
          {isTC ? (
            /* DEDICATED TRANSFER CERTIFICATE (TC) TABULAR FORMAT */
            <div style={{ width: '100%', margin: '6px 0', fontSize: '11px', fontFamily: 'Arial, sans-serif', color: '#1e293b' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #94a3b8', textAlign: 'left' }}>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #cbd5e1' }}>
                    <td style={{ width: '30px', padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '700', textAlign: 'center' }}>1.</td>
                    <td style={{ width: '45%', padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '600' }}>Name of Pupil</td>
                    <td style={{ padding: '4px 8px', fontWeight: '800', textTransform: 'uppercase', color: pColor }}>{studentData.name || 'Rahul Kumar'}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #cbd5e1', backgroundColor: '#f8fafc' }}>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '700', textAlign: 'center' }}>2.</td>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '600' }}>Father's / Guardian's Name</td>
                    <td style={{ padding: '4px 8px', fontWeight: '700' }}>{studentData.fatherName || 'Suresh Kumar'}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #cbd5e1' }}>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '700', textAlign: 'center' }}>3.</td>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '600' }}>Mother's Name</td>
                    <td style={{ padding: '4px 8px', fontWeight: '700' }}>{studentData.motherName || 'Sunita Devi'}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #cbd5e1', backgroundColor: '#f8fafc' }}>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '700', textAlign: 'center' }}>4.</td>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '600' }}>Nationality / Religion</td>
                    <td style={{ padding: '4px 8px' }}>{studentData.nationality || 'Indian'} {studentData.religion ? ` / ${studentData.religion}` : ''}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #cbd5e1' }}>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '700', textAlign: 'center' }}>5.</td>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '600' }}>Date of Birth (in figures & words)</td>
                    <td style={{ padding: '4px 8px', fontWeight: '700' }}>{studentData.dob || '15/08/2012'}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #cbd5e1', backgroundColor: '#f8fafc' }}>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '700', textAlign: 'center' }}>6.</td>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '600' }}>Date of first admission with Class</td>
                    <td style={{ padding: '4px 8px' }}>{studentData.admissionDate || '01/06/2020'}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #cbd5e1' }}>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '700', textAlign: 'center' }}>7.</td>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '600' }}>Class in which pupil last studied</td>
                    <td style={{ padding: '4px 8px', fontWeight: '700' }}>{studentData.className || 'Class 10'} (Section {studentData.section || 'A'})</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #cbd5e1', backgroundColor: '#f8fafc' }}>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '700', textAlign: 'center' }}>8.</td>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '600' }}>School / Board Annual Examination Result</td>
                    <td style={{ padding: '4px 8px', fontWeight: '700', color: '#15803d' }}>{metadata.promotionStatus || 'Passed & Promoted'}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #cbd5e1' }}>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '700', textAlign: 'center' }}>9.</td>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '600' }}>Subjects Studied</td>
                    <td style={{ padding: '4px 8px' }}>{metadata.subjects || 'English, Mathematics, Science, Social Studies, Hindi'}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #cbd5e1', backgroundColor: '#f8fafc' }}>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '700', textAlign: 'center' }}>10.</td>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '600' }}>Month up to which school dues paid</td>
                    <td style={{ padding: '4px 8px', fontWeight: '700', color: '#047857' }}>Fully Paid (No Dues)</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #cbd5e1' }}>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '700', textAlign: 'center' }}>11.</td>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '600' }}>Total school working days / Attended</td>
                    <td style={{ padding: '4px 8px' }}>{metadata.totalWorkingDays || '220'} / {metadata.daysPresent || '210'} Days ({metadata.attendancePercentage || '95.4'}%)</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #cbd5e1', backgroundColor: '#f8fafc' }}>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '700', textAlign: 'center' }}>12.</td>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '600' }}>General Conduct & Character</td>
                    <td style={{ padding: '4px 8px', fontWeight: '800', color: '#1e3a8a' }}>{metadata.conduct || 'Exemplary'}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '700', textAlign: 'center' }}>13.</td>
                    <td style={{ padding: '4px 8px', borderRight: '1px solid #cbd5e1', fontWeight: '600' }}>Reason for leaving the school</td>
                    <td style={{ padding: '4px 8px', fontWeight: '700' }}>{metadata.reasonForLeaving || 'Parent Relocation / Higher Studies'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : isComprehensiveClearance ? (
            /* COMPREHENSIVE ALL-SUBJECT & DEPARTMENTAL NO DUES CLEARANCE FORMAT */
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '9.5px', fontFamily: 'Arial, sans-serif', color: '#1e293b', margin: '2px 0' }}>
              
              {/* 1. Student Particulars Banner */}
              <div style={{
                backgroundColor: '#f8fafc',
                border: `1px solid ${pColor}35`,
                borderRadius: '4px',
                padding: '6px 10px',
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '4px 12px',
                fontSize: '9.5px',
                fontFamily: 'Arial, Helvetica, sans-serif'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  <span style={{ color: '#64748b', fontWeight: '600' }}>Student Name:</span>
                  <strong style={{ color: '#0f172a', fontWeight: '800' }}>{studentData.name || 'Student Name'}</strong>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  <span style={{ color: '#64748b', fontWeight: '600' }}>Admission No:</span>
                  <strong style={{ fontFamily: 'monospace', color: pColor, fontWeight: '800' }}>{studentData.admissionNo || studentData.studentId || 'N/A'}</strong>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  <span style={{ color: '#64748b', fontWeight: '600' }}>Roll No:</span>
                  <strong style={{ color: '#0f172a', fontWeight: '800' }}>{studentData.rollNo || 'N/A'}</strong>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  <span style={{ color: '#64748b', fontWeight: '600' }}>Class & Section:</span>
                  <strong style={{ color: '#0f172a', fontWeight: '700' }}>{studentData.className || 'Class 1'} (Section {studentData.section || 'A'})</strong>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  <span style={{ color: '#64748b', fontWeight: '600' }}>Father's Name:</span>
                  <strong style={{ color: '#0f172a', fontWeight: '700' }}>{studentData.fatherName || 'Parent Name'}</strong>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  <span style={{ color: '#64748b', fontWeight: '600' }}>Mother's Name:</span>
                  <strong style={{ color: '#0f172a', fontWeight: '700' }}>{studentData.motherName || 'Mother Name'}</strong>
                </div>
              </div>

              {/* 2. Section A: All Subject Notes & Attendance Clearance Table */}
              <div style={{ width: '100%', border: '1.5px solid #cbd5e1', borderRadius: '5px', overflow: 'hidden' }}>
                <div style={{ backgroundColor: `${pColor}12`, padding: '4px 8px', fontWeight: '800', fontSize: '10px', color: pColor, textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: '1.5px solid #cbd5e1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>📚 Section A: Subject-Wise Notes, Coursework & Teacher Clearance</span>
                  <span style={{ fontSize: '9px', fontWeight: '700', color: '#047857' }}>✓ All Notes & Coursework Completed</span>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9.5px', textAlign: 'left', fontFamily: 'Arial, Helvetica, sans-serif' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '1px solid #cbd5e1', color: '#334155', fontWeight: '700' }}>
                      <th style={{ padding: '4px 6px', width: '28px', textAlign: 'center', borderRight: '1px solid #e2e8f0' }}>Sl</th>
                      <th style={{ padding: '4px 8px', width: '26%', borderRight: '1px solid #e2e8f0' }}>Subject Name</th>
                      <th style={{ padding: '4px 8px', width: '24%', borderRight: '1px solid #e2e8f0' }}>Subject Teacher</th>
                      <th style={{ padding: '4px 6px', width: '18%', borderRight: '1px solid #e2e8f0', textAlign: 'center' }}>Notes & Homework</th>
                      <th style={{ padding: '4px 6px', width: '12%', borderRight: '1px solid #e2e8f0', textAlign: 'center' }}>Attendance</th>
                      <th style={{ padding: '4px 8px', textAlign: 'center' }}>Teacher Signature</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subjectListToRender.map((sub, idx) => (
                      <tr key={idx} style={{ borderBottom: idx < subjectListToRender.length - 1 ? '1px solid #e2e8f0' : 'none', backgroundColor: idx % 2 === 1 ? '#f8fafc' : '#ffffff' }}>
                        <td style={{ padding: '4px 6px', textAlign: 'center', fontWeight: '700', borderRight: '1px solid #e2e8f0', color: '#64748b' }}>{idx + 1}</td>
                        <td style={{ padding: '4px 8px', fontWeight: '700', color: '#0f172a', borderRight: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{sub.name}</td>
                        <td style={{ padding: '4px 8px', color: '#0f172a', borderRight: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                          {sub.teacher && sub.teacher.trim() ? (
                            <strong style={{ color: '#1e293b' }}>{sub.teacher}</strong>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>____________________</span>
                          )}
                        </td>
                        <td style={{ padding: '4px 6px', textAlign: 'center', color: '#047857', fontWeight: '700', borderRight: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                          ✓ Complete &amp; Verified
                        </td>
                        <td style={{ padding: '4px 6px', textAlign: 'center', fontWeight: '700', color: '#1e3a8a', borderRight: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                          {sub.attendance || `${metadata.attendancePercentage || '95.4'}%`}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <span style={{ border: '1px dashed #cbd5e1', borderRadius: '3px', padding: '2px 8px', fontSize: '8px', color: '#64748b', fontStyle: 'italic', fontFamily: 'Georgia, serif' }}>
                            Verified & Signed
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 3. Section B: Parent / Guardian Undertaking & Signature */}
              <div style={{
                border: `1.5px solid ${sColor}`,
                borderRadius: '5px',
                padding: '8px 12px',
                backgroundColor: '#fffbeb',
                marginTop: '6px',
                fontFamily: 'Arial, Helvetica, sans-serif'
              }}>
                <div style={{ fontSize: '10px', fontWeight: '800', color: '#92400e', textTransform: 'uppercase', marginBottom: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>✍️ Section B: Parent / Guardian Verification & Undertaking</span>
                  <span style={{ fontSize: '8.5px', color: '#b45309', fontWeight: '700' }}>Mandatory Parent Signature</span>
                </div>
                <p style={{ margin: 0, fontSize: '9.5px', color: '#78350f', lineHeight: '1.55', textAlign: 'justify' }}>
                  {`"I, ${studentData.fatherName || studentData.motherName || studentData.guardianName || 'Parent / Guardian'}, Parent/Guardian of ${studentData.name || 'the student'} (Admission No: ${studentData.admissionNo || studentData.studentId || 'N/A'}), hereby confirm that I have inspected and verified all class notes, completed homework, textbooks, and returned all school property. All academic and student obligations for the academic year ${academicYear} have been duly cleared."`}
                </p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '8px', paddingTop: '6px', borderTop: '1px dashed #d97706' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '9.5px', color: '#78350f' }}>
                    <span style={{ fontWeight: '700' }}>Parent Name:</span>
                    <span>{studentData.fatherName || studentData.motherName || 'Parent / Guardian'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '9.5px', color: '#78350f' }}>
                    <span style={{ fontWeight: '700' }}>Phone:</span>
                    <span>{studentData.fatherPhone || studentData.phone || studentData.motherPhone || '__________________'}</span>
                  </div>
                  <div style={{ width: '160px', textAlign: 'center' }}>
                    <div style={{ height: '14px', borderBottom: '1px solid #78350f' }}></div>
                    <span style={{ fontSize: '8.5px', fontWeight: '700', color: '#78350f', textTransform: 'uppercase' }}>Parent Signature & Date</span>
                  </div>
                </div>
              </div>

            </div>
          ) : (
            /* STANDARD CERTIFICATE & LETTER FORMAT */
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '16px 8px', margin: 'auto 0' }}>
              <div style={{
                fontSize: isLandscape ? '15px' : '14px',
                lineHeight: '1.9',
                textAlign: 'justify',
                textIndent: '32px',
                color: '#1e293b'
              }}>
                {renderContent()}
              </div>
            </div>
          )}

          {/* ================= SIGNATURES & STAMP FOOTER ================= */}
          <div style={{ width: '100%', paddingTop: '12px', borderTop: '1px solid #cbd5e1', marginTop: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', padding: '0 12px' }}>
              
              {/* Left: Class Teacher Signature */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                {currentTeacherSig ? (
                  <img
                    src={currentTeacherSig}
                    alt="Teacher Signature"
                    onError={() => setCurrentTeacherSig('')}
                    style={{ height: '38px', objectFit: 'contain', marginBottom: '4px' }}
                  />
                ) : (
                  <div style={{ height: '38px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                    <span style={{ fontSize: '13px', fontStyle: 'italic', fontFamily: 'Georgia, serif', color: '#1e3a8a' }}>{classTeacherName}</span>
                  </div>
                )}
                <div style={{ width: '140px', borderTop: '1px solid #0f172a', paddingTop: '3px' }}>
                  <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', color: '#0f172a', fontFamily: 'Arial, sans-serif' }}>{classTeacherName}</p>
                  <p style={{ margin: 0, fontSize: '9px', color: '#64748b', fontFamily: 'Arial, sans-serif', textTransform: 'uppercase' }}>Class Teacher</p>
                </div>
              </div>

              {/* Center: School Seal */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                {currentSeal ? (
                  <img
                    src={currentSeal}
                    alt="School Seal"
                    onError={() => setCurrentSeal('')}
                    style={{ width: '55px', height: '55px', objectFit: 'contain', opacity: 0.9, marginBottom: '2px' }}
                  />
                ) : (
                  <div style={{ width: '55px', height: '55px', border: '1.5px dashed #94a3b8', borderRadius: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontSize: '8px', color: '#64748b', fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', textAlign: 'center', padding: '2px', boxSizing: 'border-box' }}>
                    <span>Official</span>
                    <strong style={{ fontSize: '7px' }}>SEAL</strong>
                  </div>
                )}
                <span style={{ fontSize: '8px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: 'Arial, sans-serif', marginTop: '2px' }}>Affixed Seal</span>
              </div>

              {/* Right: Principal Signature */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                {currentPrincipalSig ? (
                  <img
                    src={currentPrincipalSig}
                    alt="Principal Signature"
                    onError={() => setCurrentPrincipalSig('')}
                    style={{ height: '38px', objectFit: 'contain', marginBottom: '4px' }}
                  />
                ) : (
                  <div style={{ height: '38px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                    <span style={{ fontSize: '14px', fontStyle: 'italic', fontFamily: 'Georgia, serif', color: '#1e3a8a', fontWeight: '700' }}>{principalName}</span>
                  </div>
                )}
                <div style={{ width: '140px', borderTop: '1px solid #0f172a', paddingTop: '3px' }}>
                  <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', color: '#0f172a', fontFamily: 'Arial, sans-serif', textTransform: 'uppercase' }}>{principalName}</p>
                  <p style={{ margin: 0, fontSize: '9px', color: '#64748b', fontFamily: 'Arial, sans-serif', textTransform: 'uppercase' }}>Principal / Headmaster</p>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CertificateRenderer;
